/**
 * 스토어(store-api) 전용 HTTP 클라이언트
 *
 * 의료 서버와 인증·암호화를 공유하지 않는다:
 *  - 의료 서버가 발급하는 "스토어 전용 토큰"(별도 키쌍, 짧은 수명)을 Bearer 로 붙인다
 *  - /s/ 페이로드 암호화 채널을 쓰지 않는다 (TLS 만) — 카드 정보는 PG 결제창이 처리해 서버를 지나지 않음
 *  - 토큰은 메모리에만 두고, 만료 전(30초 여유)·401 시 자동 재발급한다
 *
 * 토큰 발급 자체는 로그인 쿠키가 필요하므로 기존 의료 서버 클라이언트로 요청한다.
 */

export interface StoreHttpResponse<T = unknown> {
  data: T;
}

export interface StoreHttpClientMethod {
  get<T = unknown>(url: string): Promise<StoreHttpResponse<T>>;
  post<T = unknown>(url: string, body?: unknown): Promise<StoreHttpResponse<T>>;
  patch<T = unknown>(url: string, body?: unknown): Promise<StoreHttpResponse<T>>;
}

export interface StoreTokenIssueResult {
  storeAccessToken: string;
  expiresIn: number;
}

/** errorConverter 가 읽는 형태({ response: { status, data }, message })에 맞춘 에러 */
export class StoreHttpError extends Error {
  readonly response: { status: number; data: unknown };

  constructor(status: number, data: unknown) {
    super(`Request failed with status code ${status}`);
    this.name = "StoreHttpError";
    this.response = { status, data };
  }
}

const TOKEN_REFRESH_MARGIN_MS = 30_000;

export class StoreHttpClient implements StoreHttpClientMethod {
  private token: string | null = null;
  private expiresAt = 0;
  private pendingIssue: Promise<string> | null = null;

  constructor(private readonly issueToken: () => Promise<StoreTokenIssueResult>) {}

  async get<T = unknown>(url: string): Promise<StoreHttpResponse<T>> {
    return this.request<T>("GET", url);
  }

  async post<T = unknown>(url: string, body?: unknown): Promise<StoreHttpResponse<T>> {
    return this.request<T>("POST", url, body);
  }

  async patch<T = unknown>(url: string, body?: unknown): Promise<StoreHttpResponse<T>> {
    return this.request<T>("PATCH", url, body);
  }

  /**
   * 세션 확인 — 스토어 토큰을 실제로 발급받아 본다 (없으면 발급, 있으면 재사용).
   * 로그인 쿠키가 없거나 만료되면 의료 서버가 401 을 주므로, 라우트 가드가 이걸로 접근을 막는다.
   */
  async ensureToken(): Promise<void> {
    await this.getToken();
  }

  /** 로그아웃 등으로 세션이 바뀌면 호출 — 다음 요청에서 새 토큰을 발급받는다 */
  clearToken() {
    this.token = null;
    this.expiresAt = 0;
  }

  //MARK:- Private
  private async request<T>(
    method: "GET" | "POST" | "PATCH",
    url: string,
    body?: unknown,
    canRetry = true
  ): Promise<StoreHttpResponse<T>> {
    const token = await this.getToken();
    const response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    // 토큰 만료/폐기 → 1회 재발급 후 재시도
    if (response.status === 401 && canRetry) {
      console.log("[StoreHttpClient] :: 401 → 스토어 토큰 재발급 후 재시도");
      this.clearToken();
      return this.request<T>(method, url, body, false);
    }

    const data = await this.parseBody(response);
    if (!response.ok) {
      throw new StoreHttpError(response.status, data);
    }
    return { data: data as T };
  }

  private async getToken(): Promise<string> {
    if (this.token && Date.now() < this.expiresAt - TOKEN_REFRESH_MARGIN_MS) {
      return this.token;
    }
    // 동시 요청이 몰려도 발급은 한 번만
    if (!this.pendingIssue) {
      this.pendingIssue = this.issueToken()
        .then((result) => {
          this.token = result.storeAccessToken;
          this.expiresAt = Date.now() + result.expiresIn * 1000;
          console.log("[StoreHttpClient] :: 스토어 토큰 발급 완료 (만료 초)", result.expiresIn);
          return this.token;
        })
        .finally(() => {
          this.pendingIssue = null;
        });
    }
    return this.pendingIssue;
  }

  private async parseBody(response: Response): Promise<unknown> {
    const text = await response.text();
    if (!text) {
      return null;
    }
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }
}
